import * as React from 'react';
import cx from 'classnames';
import { I18n } from 'react-redux-i18n';
import { Provider } from 'react-redux';

import { Flex } from '@common/components/Flex';
import { Folders } from '@common/components/folders';
import { FolderManagement } from '@common/components/folder-management';
import { createFormatFolder, deleteFormat, deleteFormatFolder, getFolderFormats, getFormatFolders } from '@common/actions/formats';
import { getReviewedStatusState } from '@common/actions/invites';
import { MessageBox } from '@common/components/MessageBox';
import { Tabs } from '@common/components/tabs';
import { getTestAttributes } from '@common/utils/tests';
import { MoveFolder } from '@common/components/move-folder';
import { createStore } from '@common/utils/redux';

import { Header } from './components/header';
import { IndividualsTable } from './components/individuals-table';
import { InvitesTable } from './components/invites-table';
import { PedigreesTable } from './components/pedigrees-table';
import { useController } from './controller';
import { FolderListDto } from '@common/dto/folder/list-dto';
import { getProfiles } from '@common/actions/user-maintenance/profiles';
import { NewPedigree } from './components/new-pedigree/new-pedigree';
import { PedigreeImport } from './components/pedigree-import/pedigree-import';
import { PedigreeImport as FolderPedigreeImport } from './components/folders/components/pedigree-import';
import reducers from './components/new-pedigree/reducers';
import {
  exportPedigree,
  getPedigreeExternalLink,
  getPedigreeSecurity,
  updatePedigreeSecurity,
  openPedigreeFromPedigrees,
  applyPedigreesCurrentColumnsToAllUsers,
  setPedigreesDefaultColumnsForSpecificUsers,
  setPedigreesDefaultColumnsForToAllUsers,
  applyPedigreesCurrentColumnsToSpecificUsers,
  openPedigreeDatasheet,
} from "./services/pedigrees";
import { generateLetterFromInvites } from '@common/actions/invites';
import { generateLetterFromIndividual, getFamilyIdMembersCount, getIndividualEmail, getIndividualFamilyData, getSentQuestionnaires, openDatasheetFromIndividual, openPedigreeFromIndividual, openTestResultsFromIndividual, saveIndividualEmail, getInviteHistory } from '@common/actions/individuals';
import { applyIndividualsCurrentColumnsToAllUsers, applyIndividualsCurrentColumnsToSpecificUsers, setIndividualsDefaultColumnsForSpecificUsers, setIndividualsDefaultColumnsForToAllUsers } from './services/individuals';
import { getQuestionnaireForInvite, getQuestionnaires, openInviteQuestionnaire } from '@common/actions/questionnaires';
import { checkPedigreeNameExists, createPedigreeAndMoveToFolder, getPedigreeFolders } from '@common/actions/pedigrees';
import { createBatchLetters } from '@common/actions/letters';
import { validateIndividualEmail } from '@modules/individuals/actions/individuals';

import './pedigrees.styles.scss';

export type PedigreesProps = {

};

export const Pedigrees: React.FC<PedigreesProps> = () => {
  const {
    adminFunctions,
    filters,
    getModuleFolderFieldsForPedigreeColumn,
    getFoldersForIndividualAndInviteColumn,
    getFoldersForPedigreeColumn,
    showMessage,
    handlePedigreesSearch,
    inviteItemUniqProperty,
    invitesLoading,
    invitesSelectedItems,
    invitesLoadMetadata,
    invitesCount,
    invitesColumns,
    invites,
    handleInviteSelectedItemsChange,
    handleNewInvitesSend,
    handleRestoreInvitesColumnsToDefault,
    updateInvitesColumns,
    handleInvitesColumnsOrderChange,
    handleGetInvites,
    individualItemUniqProperty,
    individualsLoading,
    individualsSelectedItems,
    individualsLoadMetadata,
    individualsCount,
    individualsColumns,
    individuals,
    getQuestionnairesForNewQuestionnaire,
    handleLaunchNewQuestionnaire,
    handleRestoreIndividualsColumnsToDefault,
    handleIndividualRename,
    getModuleFolderFieldsForIndividualAndInviteColumn,
    updateIndividualsColumns,
    handleIndividualsColumnsOrderChange,
    handleGetIndividuals,
    getPedigreesModuleFolders,
    folders,
    loadingFolders,
    messageProps,
    folderManagementDialogProps,
    selectedFolderId,
    pedigreeItemUniqProperty,
    pedigreesCount,
    pedigreesColumns,
    pedigrees,
    pedigreesLoadMetadata,
    pedigreesSelectedItems,
    pedigreesTableOpened,
    pedigreesLoading,
    TabType,
    selectedPedigreeDetailsTab,
    defaultPedigreeDetailsTabType,
    pedigreesSearchKeyword,
    handleCopyPedigree,
    handleMovePedigree,
    handleRenamePedigree,
    handleDeletePedigrees,
    handleCreatePedigreeShortcut,
    handleRestorePedigreesColumnsToDefault,
    setSelectedPedigreeDetailsTab,
    handlePedigreeSelectedItemsChange,
    handlePedigreesColumnsOrderChange,
    updatePedigreesColumns,
    handleGetPedigreesFromTable,
    getColumnId,
    getFoldersActions,
    handleSelectedFolderChange,
    closeFolderManagementDialog,
    openNewFolderOpen,
    getFolderSecuritySettingsClasses,
    getFolder,
    showErrorMessage,
    savePedigreeFolder,
    closeMessage,
    deletePedigreeFolder,
    renameFolder,
    moveFolder,
    moveFolderDialogProps,
    closeMoveFolderDialog,
    newPedigreeModalOpened,
    pedigreeImportModalOpened,
    closePedigreeImportModal,
    openPedigreeImportModal,
    openNewPedigreeModal,
    closeNewPedigreeModal,
    showInfoMessage,
    folderPedigreeImportModalOpened,
    closeFolderPedigreeImportModal,
    handleSubmitFolderPedigreeImport,
    handleIndividualSelectedItemsChange,
    handleUpdateInvitesLockStatus,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    handleChangeSex,
    userPermissions,
    getFoldersHeaderText
  } = useController();

  const newPedigreeStore = createStore(reducers);

  const pedigreeDetailsTabs = {
    [TabType.individuals]: {
      tabProps: {
        value: TabType.individuals,
        label: I18n.t('Individuals'),
        ...getTestAttributes({ id: 'ped-home-tab-bpane-tabs-tab0' }),
      },
      component: (
        <IndividualsTable 
          filters={filters
            ? { operators: filters }
            : undefined}
          createBatchLetters={createBatchLetters}
          getIndividualFamilyData={getIndividualFamilyData}
          getFamilyIdMembersCount={getFamilyIdMembersCount}
          getPedigreeFolders={getPedigreeFolders}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          checkPedigreeNameExists={checkPedigreeNameExists}
          getQuestionnaires={getQuestionnaires}
          sendInvite={(x) => handleNewInvitesSend([x])}
          getIndividualEmail={getIndividualEmail}
          saveIndividualEmail={saveIndividualEmail}
          getSentQuestionnaires={getSentQuestionnaires}
          onOpenInviteQuestionnaire={openInviteQuestionnaire}
          getQuestionnairesForNewQuestionnaire={getQuestionnairesForNewQuestionnaire}
          onLaunchNewQuestionnaire={handleLaunchNewQuestionnaire}
          adminFunctions={adminFunctions}
          onApplyCurrentColumnsToSpecificUsers={applyIndividualsCurrentColumnsToSpecificUsers}
          onApplyCurrentColumnsToAllUsers={applyIndividualsCurrentColumnsToAllUsers} 
          onSetDefaultColumnsForSpecificUsers={setIndividualsDefaultColumnsForSpecificUsers}
          onSetDefaultColumnsForAllUsers={setIndividualsDefaultColumnsForToAllUsers}
          onRestoreColumnsToDefault={handleRestoreIndividualsColumnsToDefault}
          openDatasheet={openDatasheetFromIndividual}
          openPedigree={openPedigreeFromIndividual}
          openTestResults={openTestResultsFromIndividual}
          generateLetter={generateLetterFromIndividual}
          itemUniqProperty={individualItemUniqProperty}
          loading={individualsLoading}
          selectedItems={individualsSelectedItems}
          loadMetadata={individualsLoadMetadata}
          totalItemsCount={individualsCount}
          className="Pedigrees__content__tables__pedigree-details__information"
          columns={individualsColumns}
          data={individuals}
          getColumnId={getColumnId}
          onColumnsResize={updateIndividualsColumns}
          showErrorMessage={showErrorMessage}
          closeMessage={closeMessage}
          showMessage={showMessage}
          showInfoMessage={showInfoMessage}
          onColumnsOrderChange={handleIndividualsColumnsOrderChange}
          onSelectedItemsChange={handleIndividualSelectedItemsChange}
          getProfiles={getProfiles}
          onIndividualRename={handleIndividualRename}
          getFields={getModuleFolderFieldsForIndividualAndInviteColumn}
          getFoldersForAddingColumns={getFoldersForIndividualAndInviteColumn}
          changeSex={handleChangeSex}
          getData={(loadMetadata) => { 
            if (pedigreesSelectedItems.length) {
              return handleGetIndividuals({
                columns: individualsColumns,
                pedigreeId: pedigreesSelectedItems[0]?.id!,
                loadedIndividuals: individuals,
                ...loadMetadata,
              });
            }
          }}
        />
      )
    },
    [TabType.invites]: {
      tabProps: {
        value: TabType.invites,
        label: I18n.t('Invites'),
        ...getTestAttributes({ id: 'ped-home-tab-bpane-tabs-tab1' }),
      },
      component: (
        <InvitesTable 
          validateIndividualEmail={validateIndividualEmail}
          createBatchLetters={createBatchLetters}
          adminFunctions={adminFunctions}
          filters={filters
            ? { operators: filters }
            : undefined}
          getQuestionnaireForInvite={getQuestionnaireForInvite}
          getSentQuestionnaires={getSentQuestionnaires}
          onOpenInviteQuestionnaire={openInviteQuestionnaire}
          getIndividualFamilyData={getIndividualFamilyData}
          getFamilyIdMembersCount={getFamilyIdMembersCount}
          getPedigreeFolders={getPedigreeFolders}
          createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
          checkPedigreeNameExists={checkPedigreeNameExists}
          getQuestionnaires={getQuestionnaires}
          sendInvites={handleNewInvitesSend}
          getIndividualEmail={getIndividualEmail}
          saveIndividualEmail={saveIndividualEmail}
          itemUniqProperty={inviteItemUniqProperty}
          getInviteHistory={getInviteHistory}
          loading={invitesLoading}
          selectedItems={invitesSelectedItems}
          loadMetadata={invitesLoadMetadata}
          totalItemsCount={invitesCount}
          columns={invitesColumns}
          className="Pedigrees__content__tables__pedigree-details__information"
          data={invites}
          onRestoreColumnsToDefault={handleRestoreInvitesColumnsToDefault}
          getColumnId={getColumnId}
          openPedigree={openPedigreeFromIndividual}
          openDatasheet={openDatasheetFromIndividual}
          onColumnsResize={updateInvitesColumns}
          showInfoMessage={showInfoMessage}
          closeMessage={closeMessage}
          showMessage={showMessage}
          showErrorMessage={showErrorMessage}
          onColumnsOrderChange={handleInvitesColumnsOrderChange}
          onSelectedItemsChange={handleInviteSelectedItemsChange}
          getFields={getModuleFolderFieldsForIndividualAndInviteColumn}
          getFoldersForAddingColumns={getFoldersForIndividualAndInviteColumn}
          generateLetter={generateLetterFromInvites}
          updateInvitesLockStatus={handleUpdateInvitesLockStatus}
          getReviewedStatusState={getReviewedStatusState}
          updateReviewedStatusActions={handleUpdateReviewedStatusActions}
          updateInviteStatus={handleUpdateInviteStatus}
          getData={(loadMetadata) => {
            if (pedigreesSelectedItems.length) {
              return handleGetInvites({
                columns: invitesColumns,
                pedigreeId: pedigreesSelectedItems[0]?.id!,
                loadedInvites: invites,
                ...loadMetadata,
              });
            }
          }}
        />
      )
    }
  };
  return (
    <div className="Pedigrees">
      <MessageBox
        hideMessageBox={closeMessage}
        {...messageProps}
      />
      <Header 
        pedigreesSearchKeyword={pedigreesSearchKeyword}
        className="Pedigrees__header"
        onImportPedigreeClick={openPedigreeImportModal}
        onNewPedigreeClick={openNewPedigreeModal}
        onFiltersChange={handlePedigreesSearch}
      />
      {newPedigreeModalOpened && (
        <Provider store={newPedigreeStore}>
          <NewPedigree 
            close={closeNewPedigreeModal} 
            currentFolderId={selectedFolderId} 
          />
        </Provider>
      )}
      {pedigreeImportModalOpened && (
        <PedigreeImport
          close={closePedigreeImportModal} 
          folderId={selectedFolderId} 
        /> 
      )}
      {folderPedigreeImportModalOpened && (
        <FolderPedigreeImport
          onClose={closeFolderPedigreeImportModal}
          onSubmit={handleSubmitFolderPedigreeImport}
          showErrorMessage={showErrorMessage}
        />
      )}
      {folderManagementDialogProps.opened && (
        <FolderManagement
          mode={folderManagementDialogProps.mode}
          folders={folders}
          folder={folderManagementDialogProps.folder}
          showErrorMessage={showErrorMessage}
          onClose={closeFolderManagementDialog}
          onSave={savePedigreeFolder}
          getFolderSecuritySettingsClasses={getFolderSecuritySettingsClasses}
          getFolder={getFolder}
          getFolderFormats={getFolderFormats}
          getFormatFolders={getFormatFolders}
          onCreateFormatFolder={createFormatFolder}
          onDeleteFormatFolder={deleteFormatFolder}
          onDeleteFormat={deleteFormat}
        />
      )}
      {moveFolderDialogProps.opened && (
        <MoveFolder
          folders={folders}
          folder={moveFolderDialogProps.folder as unknown as FolderListDto}
          showErrorMessage={showErrorMessage}
          onSubmit={moveFolder}
          onClose={closeMoveFolderDialog}
        />
      )}
      <Flex width="100%" wrap="nowrap" className="Pedigrees__content">
        <Folders 
          getId={{
            addFolder: 'ped-home-tab-tree-add-folder-btn',
            treeFolder: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
              ? `ped-home-tab-tree-folder-${ownIndex}` 
              : `ped-home-tab-tree-folder-child-${data.id}`,
            treeCollapseIcon: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
              ? `ped-home-tab-tree-folder-${ownIndex}-exp-icon` 
              : `ped-home-tab-tree-folder-child-${data.id}-exp-icon`,
            treeActionBtn: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
              ? `ped-home-tab-tree-folder-${ownIndex}-con-icon` 
              : `ped-home-tab-tree-folder-child-${data.id}-exp-icon`,
          }}
          className="Pedigrees__content__folders"
          list={folders}
          loading={loadingFolders}
          selectedFolderId={selectedFolderId}
          totalItemsCount={pedigreesCount}
          onChange={handleSelectedFolderChange}
          onNewFolderClick={openNewFolderOpen}
          onRenameFolderClick={renameFolder}
          onDeleteFolderClick={deletePedigreeFolder}
          getFoldersActions={getFoldersActions}
          userPermissions={userPermissions}
          getHeaderText={getFoldersHeaderText}
        />
        <div className="Pedigrees__content__tables">
          <PedigreesTable 
            itemUniqProperty={pedigreeItemUniqProperty}
            className={cx(
              'Pedigrees__content__tables__pedigrees',
              {
                'Pedigrees__content__tables__pedigrees--opened': !pedigreesTableOpened
              }
            )}
            adminFunctions={adminFunctions}
            getFoldersForPedigreeActions={getPedigreesModuleFolders}
            onApplyCurrentColumnsToSpecificUsers={applyPedigreesCurrentColumnsToSpecificUsers}
            onApplyCurrentColumnsToAllUsers={applyPedigreesCurrentColumnsToAllUsers} 
            onSetDefaultColumnsForSpecificUsers={setPedigreesDefaultColumnsForSpecificUsers}
            onSetDefaultColumnsForAllUsers={setPedigreesDefaultColumnsForToAllUsers}
            onRestoreColumnsToDefault={handleRestorePedigreesColumnsToDefault}
            filters={filters
              ? { operators: filters }
              : undefined}
            closeMessage={closeMessage}
            showMessage={showMessage}
            selectedFolderId={selectedFolderId}
            getProfiles={getProfiles}
            getFields={getModuleFolderFieldsForPedigreeColumn}
            getFoldersForAddingColumns={getFoldersForPedigreeColumn}
            getPedigreeSecurity={getPedigreeSecurity}
            updatePedigreeSecurity={updatePedigreeSecurity}
            loading={pedigreesLoading}
            openPedigree={openPedigreeFromPedigrees}
            openPedigreeDatasheet={openPedigreeDatasheet}
            exportPedigree={exportPedigree}
            selectedItems={pedigreesSelectedItems}
            loadMetadata={pedigreesLoadMetadata}
            totalItemsCount={pedigreesCount}
            columns={pedigreesColumns}
            data={pedigrees}
            getColumnId={getColumnId}
            onColumnsResize={updatePedigreesColumns}
            showInfoMessage={showInfoMessage}
            showErrorMessage={showErrorMessage}
            getPedigreeExternalLink={getPedigreeExternalLink}
            onColumnsOrderChange={handlePedigreesColumnsOrderChange}
            onSelectedItemsChange={handlePedigreeSelectedItemsChange}
            getData={handleGetPedigreesFromTable}
            onCopyPedigree={handleCopyPedigree}
            onCreateShortcut={handleCreatePedigreeShortcut}
            onDeletePedigrees={handleDeletePedigrees}
            onMovePedigree={handleMovePedigree}
            onRenamePedigree={handleRenamePedigree}
          />
          <div 
            className={cx(
              'Pedigrees__content__tables__pedigree-details',
              {
                'Pedigrees__content__tables__pedigree-details--opened': pedigreesTableOpened 
              }
            )}
          >
            <Flex alignItems="flex-end" className="Pedigrees__content__tables__pedigree-details__header">
              <Tabs 
                defaultValue={defaultPedigreeDetailsTabType}
                items={Object.values(pedigreeDetailsTabs).map((x) => x.tabProps)}
                onChange={setSelectedPedigreeDetailsTab}
              />

              <button 
                onClick={() => setSelectedPedigreeDetailsTab(x => x ? undefined : defaultPedigreeDetailsTabType)}
                className="Pedigrees__content__tables__pedigree-details__toggle-btn"
                {...getTestAttributes({ id: 'ped-home-tab-bpane-collapse-btn' })}
              >
                <i className={cx('fa', pedigreesTableOpened ? 'fa-angle-down' : 'fa-angle-up' )} />
              </button>
            </Flex>
            {pedigreesTableOpened && selectedPedigreeDetailsTab && pedigreeDetailsTabs[selectedPedigreeDetailsTab].component}
          </div>
        </div>
      </Flex>
    </div>
  );
};